import type { ReactNode } from 'react';
import classNames from 'classnames';

import styles from './Eyebrow.module.scss';

/** The elements a label is written as: a heading, a list term, a legend, or plain text. */
export type EyebrowElement =
    | 'span'
    | 'div'
    | 'p'
    | 'label'
    | 'legend'
    | 'dt'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6';

export type EyebrowProps = {
    children: ReactNode;
    /** The element to render; `span` by default. */
    as?: EyebrowElement;
    /** Margins, layout or a line height; the type comes from the component. */
    className?: string;
    id?: string;
    /** The control a `label` names. */
    htmlFor?: string;
};

export type OverlineSize = 'sm' | 'md';

function Eyebrow(props: EyebrowProps & { look: string }) {
    const { children, as: Tag = 'span', className, id, htmlFor, look } = props;
    const elementClass = classNames(look, className);

    if (Tag === 'label') {
        return (
            <label className={elementClass} id={id} htmlFor={htmlFor}>
                {children}
            </label>
        );
    }
    return (
        <Tag className={elementClass} id={id}>
            {children}
        </Tag>
    );
}

/** The uppercase label over a metric value. */
export function Caption(props: EyebrowProps) {
    return <Eyebrow {...props} look={styles.caption} />;
}

/** A tracked, medium-weight uppercase label that heads a group or a section: `sm` (12px) for
    a group or a field, `md` (14px) for a panel title. */
export function Overline(props: EyebrowProps & { size?: OverlineSize }) {
    const { size = 'sm', ...rest } = props;
    return <Eyebrow {...rest} look={size === 'md' ? styles.overlineMd : styles.overlineSm} />;
}
